/**
 * Outlet Authentication Provider
 * 
 * Manages outlet and role authentication state across the booth app.
 * Provides outlet context to all components and handles authentication lifecycle.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import type { Outlet, OutletConfiguration, APIResponse, ValidatePinResponse } from '@photobooth/types';
import { loadAuthState, saveAuthState, clearAuthState, DeviceRole, StoredAuthState, isStorageAvailable } from './storage';
import { logAuthChange } from './auth-logger';
import { initializeWebSocketClient, getWebSocketClient } from '../sync/websocket-client';
import { refreshFramesCache } from '../db/frames';

// API client will be injected via props
interface OutletAuthAPI {
  // Optional token setter – implemented by PhotoBoothAPI, mocked in tests
  setAuthToken?: (token: string | null) => void;
  outlets: {
    getById: (outletId: string) => Promise<APIResponse<Outlet>>;
    validatePin: (
      outletId: string,
      pin: string,
      role?: 'customer' | 'capture'
    ) => Promise<APIResponse<ValidatePinResponse>>;
    // We intentionally keep this loosely typed to avoid tight coupling
    // to the exact OutletPublicInfo shape from @photobooth/types.
    // The implementation only uses a small subset of the fields.
    getActiveOutlets: () => Promise<APIResponse<any[]>>;
  };
  auth: {
    cashierLogin: (data: { username: string; password: string; outletId: string }) => Promise<APIResponse<{
      success: boolean;
      data: {
        token: string;
        expiresAt: Date;
        user: {
          userId: string;
          username: string;
          fullName?: string;
          email?: string;
          role: 'cashier';
          outletId: string;
        };
      };
    }>>;
  };
}

export interface OutletAuthState {
  outletId: string;
  outletName: string;
  outletLocation: string;
  role: DeviceRole;
  authenticatedAt: number;
  // Cashier-specific fields (only present when role is 'cashier')
  userId?: string;
  username?: string;
  fullName?: string;
}

export interface OutletAuthContextValue {
  // State
  isAuthenticated: boolean;
  isLoading: boolean;
  authState: OutletAuthState | null;
  outlet: Outlet | null;
  sessionOnlyMode: boolean; // NEW: indicates storage is unavailable
  
  // Actions
  authenticate: (outletId: string, role: DeviceRole, pin: string) => Promise<void>;
  authenticateCashier: (outletId: string, username: string, password: string) => Promise<void>;
  clearAuthentication: () => Promise<void>;
  validateAuthentication: () => Promise<boolean>;
  
  // Role checks
  hasRole: (role: DeviceRole) => boolean;
  canAccessRoute: (route: string) => boolean;
}

const OutletAuthContext = createContext<OutletAuthContextValue | null>(null);

interface OutletAuthProviderProps {
  children: ReactNode;
  apiClient: OutletAuthAPI;
}

// Route-to-role mapping
const ROUTE_ROLE_MAP: Record<string, DeviceRole | DeviceRole[]> = {
  '/': 'customer',
  '/order': 'customer',
  '/edit': 'customer',
  '/capture': 'capture',
  '/cashier': 'cashier',
  '/settings': ['customer', 'capture', 'cashier'], // All roles can access settings
};

export function OutletAuthProvider({ children, apiClient }: OutletAuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authState, setAuthState] = useState<OutletAuthState | null>(null);
  const [outlet, setOutlet] = useState<Outlet | null>(null);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [sessionOnlyMode, setSessionOnlyMode] = useState(false);

  /**
   * Check storage availability on mount
   */
  useEffect(() => {
    const available = isStorageAvailable();
    setStorageAvailable(available);
    
    if (!available) {
      console.warn('localStorage is not available - running in session-only mode');
      setSessionOnlyMode(true);
    }
  }, []);

  /**
   * Load authentication state from storage and validate on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        
        // Load from localStorage
        const stored = loadAuthState();
        if (!stored) {
          setIsLoading(false);
          return;
        }
        // Validate outlet via API
        const isValid = await validateAuthenticationInternal(stored);
        
        if (!isValid) {
          // Clear invalid authentication
          clearAuthState();
          setIsAuthenticated(false);
          setAuthState(null);
          setOutlet(null);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing authentication:', error);
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  /**
   * Listen for online event and validate authentication in background
   */
  useEffect(() => {
    const handleOnline = async () => {
      console.info('Network connection restored, validating authentication...');
      
      // Only validate if we're currently authenticated
      if (!isAuthenticated || !authState) {
        return;
      }

      try {
        // Load current auth state
        const stored = loadAuthState();
        if (!stored) {
          return;
        }
        // Validate current authentication
        const isValid = await validateAuthenticationInternal(stored);
        
        if (!isValid) {
          console.warn('Authentication validation failed after reconnect');
          // Clear invalid authentication
          clearAuthState();
          setIsAuthenticated(false);
          setAuthState(null);
          setOutlet(null);
          
          // Log logout
          logAuthChange(
            stored.outletId,
            null,
            stored.role,
            null,
            'logout'
          );
        } else {
          console.info('Authentication validated successfully after reconnect');
        }
      } catch (error) {
        console.error('Error validating authentication on reconnect:', error);
      }
    };

    window.addEventListener('online', handleOnline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated, authState]);

  /**
   * Internal validation helper with retry logic
   */
  const validateAuthenticationInternal = async (
    stored: StoredAuthState,
    retryCount = 0
  ): Promise<boolean> => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000; // Start with 1 second
    
    try {
      // Validate outlet still exists and is active.
      // Use public active outlets endpoint so this works for all roles
      // without requiring admin privileges or a persisted token.
      const outletsResponse = await apiClient.outlets.getActiveOutlets();

      if (!outletsResponse.success || !outletsResponse.data) {
        console.warn('Outlet validation failed: could not load active outlets');
        return false;
      }

      const publicOutlet = outletsResponse.data.find(
        (o: any) => o.outletId === stored.outletId
      );

      if (!publicOutlet) {
        console.warn('Outlet validation failed: outlet not found in active outlets');
        return false;
      }

      const status = (publicOutlet as any).status ?? 'active';
      if (status !== 'active') {
        console.warn('Outlet validation failed: outlet is inactive');
        return false;
      }

      // Normalize public outlet data into Outlet shape
      const outletData: Outlet = {
        outletId: publicOutlet.outletId,
        slug: (publicOutlet as any).slug || publicOutlet.outletId,
        name: publicOutlet.name,
        location: publicOutlet.location,
        address: publicOutlet.address ?? undefined,
        mapsUrl: (publicOutlet as any).mapsUrl ?? undefined,
        imageUrl: (publicOutlet as any).imageUrl ?? undefined,
        status,
        isPublic: (publicOutlet as any).isPublic ?? true,
        configuration:
          ((publicOutlet as any).configuration as OutletConfiguration) ?? { syncInterval: 30 },
        productIds: (publicOutlet as any).productIds,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Update state
      setAuthState({
        outletId: stored.outletId,
        outletName: outletData.name,
        outletLocation: outletData.location,
        role: stored.role,
        authenticatedAt: stored.authenticatedAt,
        // Restore cashier details if present
        userId: stored.userId,
        username: stored.username,
        fullName: stored.fullName,
      });
      setOutlet(outletData);
      setIsAuthenticated(true);

      // Restore WebSocket connection based on stored role and token so that
      // device counts stay accurate after app reload.
      try {
        let wsToken: string | null = null;
        let terminalRole: 'order' | 'capture' | 'edit' | null = null;

        if (stored.role === 'cashier') {
          // Cashier uses the cashier auth token
          wsToken = typeof localStorage !== 'undefined'
            ? localStorage.getItem('photobooth_auth_token')
            : null;
          terminalRole = 'order';
        } else if (stored.role === 'customer' || stored.role === 'capture') {
          // Customer/Capture use the device token from PIN validation
          wsToken = typeof localStorage !== 'undefined'
            ? localStorage.getItem('photobooth_device_token')
            : null;
          terminalRole = stored.role === 'capture' ? 'capture' : 'order';
        }

        if (wsToken) {
          // Set auth token on API client for subsequent requests
          if (apiClient.setAuthToken) {
            apiClient.setAuthToken(wsToken);
          }
          
          if (terminalRole) {
            await initializeWebSocketClient(wsToken, stored.outletId, terminalRole);
          }
        }
      } catch (wsError) {
        console.error('Failed to initialize WebSocket client after auth restore:', wsError);
      }

      return true;
    } catch (error) {
      console.error('Error validating authentication:', error);
      
      // Check if we should retry
      const isNetworkError = error instanceof Error && 
        (error.message.includes('network') || 
         error.message.includes('fetch') ||
         error.message.includes('timeout'));
      
      if (isNetworkError && retryCount < MAX_RETRIES) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount);
        console.info(`Retrying validation in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return validateAuthenticationInternal(stored, retryCount + 1);
      }
      
      return false;
    }
  };

  /**
   * Authenticate cashier with username and password
   */
  const authenticateCashier = useCallback(async (outletId: string, username: string, password: string): Promise<void> => {
    try {
      // Store old auth state for logging
      const oldAuthState = authState;

      // Authenticate via API
      const response = await apiClient.auth.cashierLogin({
        username,
        password,
        outletId,
      });
      
      // Handle API errors
      if (!response.success) {
        const error = response.error;
        const errorCode = error?.code;
        
        // Handle specific error codes (HTTP status codes are passed as strings)
        if (errorCode === '404') {
          throw new Error('Outlet not found. Please select a different outlet.');
        }
        
        if (errorCode === '401') {
          throw new Error('Invalid credentials. Please try again.');
        }
        
        if (errorCode === '403') {
          throw new Error('You do not have access to this outlet. Please contact your administrator.');
        }
        
        if (errorCode === '429') {
          throw new Error('Too many attempts. Please wait a moment and try again.');
        }
        
        if (errorCode === '500' || errorCode === '503') {
          throw new Error('Server error. Please try again in a moment.');
        }
        
        // Generic error
        throw new Error(error?.message || 'Authentication failed. Please try again.');
      }
      
      if (!response.data) {
        throw new Error('No response data received from server');
      }

      const { token, user } = response.data.data;

      // Set auth token on API client so subsequent requests include Authorization header
      // This is important for endpoints that require authenticated cashier/admin access.
      if (apiClient.setAuthToken) {
        apiClient.setAuthToken(token);
      }
      // Persist token for other API clients (e.g. cashier dashboard) and across refresh
      try {
        localStorage.setItem('photobooth_auth_token', token);
      } catch {
        // Non-critical: token persistence is best-effort
      }

      // Initialize WebSocket connection for cashier device so that
      // the admin dashboard can see this cashier as an online device.
      try {
        await initializeWebSocketClient(token, outletId, 'order');
      } catch (wsError) {
        console.error('Failed to initialize WebSocket client for cashier:', wsError);
      }

      // Fetch outlet details using public active outlets endpoint
      // This avoids requiring admin privileges for the cashier flow.
      const outletsResponse = await apiClient.outlets.getActiveOutlets();
      if (!outletsResponse.success || !outletsResponse.data) {
        throw new Error('Failed to fetch outlet details');
      }

      const publicOutlet = outletsResponse.data.find(
        (o) => o.outletId === outletId
      );
      if (!publicOutlet) {
        throw new Error('Outlet not found or not active');
      }

      // Normalize public outlet data into full Outlet shape
      const outletData = {
        outletId: publicOutlet.outletId,
        slug: (publicOutlet as any).slug || publicOutlet.outletId,
        name: publicOutlet.name,
        location: publicOutlet.location,
        address: publicOutlet.address,
        status: ((publicOutlet as any).status as 'active' | 'inactive') || 'active',
        isPublic: (publicOutlet as any).isPublic ?? true,
        configuration: ((publicOutlet as any).configuration ||
          { syncInterval: 30 }) as unknown as OutletConfiguration,
        productIds: (publicOutlet as any).productIds,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save to localStorage (with error handling)
      const authData: Omit<StoredAuthState, 'version'> = {
        outletId,
        role: 'cashier',
        authenticatedAt: Date.now(),
        userId: user.userId,
        username: user.username,
        fullName: user.fullName,
      };
      
      if (storageAvailable) {
        try {
          saveAuthState(authData);
        } catch (storageError) {
          console.error('Failed to save auth state to localStorage:', storageError);
          
          // Continue in session-only mode
          setSessionOnlyMode(true);
          
          // Show warning to user (non-blocking)
          console.warn('Authentication will not persist across app restarts');
        }
      }

      // Update state
      setAuthState({
        outletId,
        outletName: outletData.name,
        outletLocation: outletData.location,
        role: 'cashier',
        authenticatedAt: authData.authenticatedAt,
        userId: user.userId,
        username: user.username,
        fullName: user.fullName,
      });
      setOutlet(outletData);
      setIsAuthenticated(true);

      // Log authentication change
      const changeType = !oldAuthState 
        ? 'initial_auth' 
        : oldAuthState.outletId !== outletId 
          ? 'outlet_change' 
          : 'role_change';
      
      logAuthChange(
        oldAuthState?.outletId || null,
        outletId,
        oldAuthState?.role || null,
        'cashier',
        changeType
      );

    } catch (error) {
      console.error('Cashier authentication error:', error);
      
      // Re-throw with user-friendly message
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Authentication failed. Please try again.');
    }
  }, [apiClient, authState, storageAvailable]);

  /**
   * Authenticate with outlet, role, and PIN
   */
  const authenticate = useCallback(async (outletId: string, role: DeviceRole, pin: string): Promise<void> => {
    try {
      // Store old auth state for logging
      const oldAuthState = authState;

      // Validate PIN via API with role-aware payload
      const roleForPin = role === 'customer' || role === 'capture' ? role : undefined;
      const response = await apiClient.outlets.validatePin(outletId, pin, roleForPin);
      
      // Handle API errors
      if (!response.success) {
        const error = response.error;
        const errorCode = error?.code;
        
        // Handle specific error codes (HTTP status codes are passed as strings)
        if (errorCode === '404') {
          throw new Error('Outlet not found. Please select a different outlet.');
        }
        
        if (errorCode === '401') {
          throw new Error('Invalid PIN. Please try again.');
        }
        
        if (errorCode === '403') {
          throw new Error('This outlet is not active. Please contact your administrator.');
        }
        
        if (errorCode === '429') {
          throw new Error('Too many attempts. Please wait a moment and try again.');
        }
        
        if (errorCode === '500' || errorCode === '503') {
          throw new Error('Server error. Please try again in a moment.');
        }
        
        // Generic error
        throw new Error(error?.message || 'PIN validation failed. Please try again.');
      }
      
      if (!response.data) {
        throw new Error('No response data received from server');
      }

      if (!response.data.valid) {
        throw new Error(response.data.message || 'Invalid PIN');
      }

      if (!response.data.outlet) {
        throw new Error('Outlet data not returned from API');
      }

      const outletData = response.data.outlet;

      // Check if outlet is active
      if (outletData.status !== 'active') {
        throw new Error('Outlet is not active');
      }

      // Save to localStorage (with error handling)
      const authData: Omit<StoredAuthState, 'version'> = {
        outletId,
        role,
        authenticatedAt: Date.now(),
      };
      
      if (storageAvailable) {
        try {
          saveAuthState(authData);
        } catch (storageError) {
          console.error('Failed to save auth state to localStorage:', storageError);
          
          // Continue in session-only mode
          setSessionOnlyMode(true);
          
          // Show warning to user (non-blocking)
          console.warn('Authentication will not persist across app restarts');
        }
      }

      // Update state
      setAuthState({
        outletId,
        outletName: outletData.name,
        outletLocation: outletData.location,
        role,
        authenticatedAt: authData.authenticatedAt,
      });
      setOutlet({
        outletId: outletData.outletId,
        slug: outletData.outletId, // Use outletId as slug fallback
        name: outletData.name,
        location: outletData.location,
        address: outletData.address,
        status: outletData.status,
        isPublic: false, // Default to false for PIN auth
        configuration: (outletData.configuration || { syncInterval: 30 }) as unknown as OutletConfiguration,
        createdAt: outletData.createdAt,
        updatedAt: outletData.updatedAt,
      });
      setIsAuthenticated(true);

      // Initialize WebSocket connection for this device (customer or capture)
      // so the admin dashboard can track live device counts.
      try {
        const deviceToken = response.data.token;
        if (deviceToken) {
          // Set auth token on API client so subsequent requests include Authorization header
          if (apiClient.setAuthToken) {
            apiClient.setAuthToken(deviceToken);
          }
          
          try {
            localStorage.setItem('photobooth_device_token', deviceToken);
          } catch {
            // Non-critical: token persistence is best-effort
          }
          const terminalRole = role === 'capture' ? 'capture' : 'order';
          await initializeWebSocketClient(deviceToken, outletId, terminalRole);
        }
      } catch (wsError) {
        console.error('Failed to initialize WebSocket client for device:', wsError);
      }

      // Log authentication change
      const changeType = !oldAuthState 
        ? 'initial_auth' 
        : oldAuthState.outletId !== outletId 
          ? 'outlet_change' 
          : 'role_change';
      
      logAuthChange(
        oldAuthState?.outletId || null,
        outletId,
        oldAuthState?.role || null,
        role,
        changeType
      );

      // Update local frame cache when customer terminal logs in so new frames from admin appear
      if (role === 'customer') {
        refreshFramesCache().catch((err) =>
          console.warn('Background frame cache refresh failed:', err)
        );
      }

    } catch (error) {
      console.error('Authentication error:', error);
      
      // Re-throw with user-friendly message
      if (error instanceof Error) {
        throw error;
      }
      
      throw new Error('Authentication failed. Please try again.');
    }
  }, [apiClient, authState, storageAvailable]);

  /**
   * Clear authentication and logout
   */
  const clearAuthentication = useCallback(async (): Promise<void> => {
    try {
      // Store old auth state for logging
      const oldAuthState = authState;

      clearAuthState();
      setIsAuthenticated(false);
      setAuthState(null);
      setOutlet(null);

      // Clear cached auth token used by other API clients
      try {
        localStorage.removeItem('photobooth_auth_token');
        localStorage.removeItem('photobooth_device_token');
      } catch {
        // Non-critical
      }
      if (apiClient.setAuthToken) {
        apiClient.setAuthToken(null);
      }

      // Best-effort: disconnect WebSocket client if it was initialized
      try {
        const wsClient = getWebSocketClient();
        wsClient.disconnect();
      } catch {
        // If WebSocket client was never initialized, ignore
      }

      // Log logout
      logAuthChange(
        oldAuthState?.outletId || null,
        null,
        oldAuthState?.role || null,
        null,
        'logout'
      );
    } catch (error) {
      console.error('Error clearing authentication:', error);
      throw error;
    }
  }, [authState]);

  /**
   * Validate current authentication
   */
  const validateAuthentication = useCallback(async (): Promise<boolean> => {
    try {
      const stored = loadAuthState();
      if (!stored) {
        return false;
      }

      return await validateAuthenticationInternal(stored);
    } catch (error) {
      console.error('Error validating authentication:', error);
      return false;
    }
  }, [apiClient]);

  /**
   * Check if current role matches
   */
  const hasRole = useCallback((role: DeviceRole): boolean => {
    return authState?.role === role;
  }, [authState]);

  /**
   * Check if current role can access a route
   */
  const canAccessRoute = useCallback((route: string): boolean => {
    if (!authState) {
      return false;
    }

    // Find matching route pattern
    const matchingRoute = Object.keys(ROUTE_ROLE_MAP).find(pattern => {
      if (pattern === route) return true;
      if (route.startsWith(pattern + '/')) return true;
      return false;
    });

    if (!matchingRoute) {
      // No specific rule, allow if authenticated
      return true;
    }

    const allowedRoles = ROUTE_ROLE_MAP[matchingRoute];
    
    if (Array.isArray(allowedRoles)) {
      return allowedRoles.includes(authState.role);
    }
    
    return authState.role === allowedRoles;
  }, [authState]);

  const contextValue: OutletAuthContextValue = {
    isAuthenticated,
    isLoading,
    authState,
    outlet,
    sessionOnlyMode,
    authenticate,
    authenticateCashier,
    clearAuthentication,
    validateAuthentication,
    hasRole,
    canAccessRoute,
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-bounce">📸</div>
          <div className="text-xl font-bold text-gray-700">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <OutletAuthContext.Provider value={contextValue}>
      {children}
    </OutletAuthContext.Provider>
  );
}

/**
 * Hook to access outlet authentication context
 * @throws Error if used outside OutletAuthProvider
 */
export function useOutletAuth(): OutletAuthContextValue {
  const context = useContext(OutletAuthContext);
  if (!context) {
    throw new Error('useOutletAuth must be used within OutletAuthProvider');
  }
  return context;
}

/**
 * Default export with API client initialization
 * This wrapper creates the API client automatically
 */
import { createPhotoBoothAPI } from '@photobooth/api-client';
import { getEffectiveApiBaseUrl } from '../config/terminal-urls';

export default function OutletAuthProviderWithClient({ children }: { children: ReactNode }) {
  const [apiClient] = useState(() => {
    return createPhotoBoothAPI({
      baseUrl: getEffectiveApiBaseUrl(),
    });
  });

  // Note: We can't initialize sync manager here because PhotoBoothAPI doesn't expose the internal APIClient
  // The sync manager should be initialized elsewhere with the proper APIClient instance

  return (
    <OutletAuthProvider apiClient={apiClient}>
      {children}
    </OutletAuthProvider>
  );
}
